import React, { useEffect, useLayoutEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import TextInput from "@components/textInput/TextInput";
import DatePicker from "@components/datePicker/DatePicker";
import DropDown from "@components/dropDown/DropDown";
import RadioBottom from "@components/radioBottom/RadioBottom";
import ButtonLoader from "@components/ButtonLoader/ButtonLoader";
import ScheduleStudent from "./ScheduleStudent";
import PostalCode from "./PostalCode";

import "./AddAndEditChild.scss";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
import { useMst } from "@store/Route";
import CalculationCostResult from "./CalculationCost";
import ImageInput from "@components/imageInput/ImageInput";
import useAddAndEditChild from "./components/useAddAndEditChild";
import EditChildComponent from "./components/EditChildComponent";

interface ParamTypes {
  id?: string;
}

// setFieldValue
const AddAndEditChild = observer(() => {
  // edit id not null / undefined
  const { id } = useParams<ParamTypes>();
  const history = useHistory();
  const {
    schools,
    landing,
    academicTerms,
    holidays,
    postalCodes,
    calculationCost,
    students,
    schedulePermissions,
  } = useMst();

  const { token, userId } = landing;

  const {
    getListSchools,
    setSelectSchool,
    selectSchool,
    getAcademicYears,
    listAcademicYears,
    getLevelSchools,
    listSchoolNames,
    listLevelSchools,
    getClassSchedules,
    listClassSchedules,
    fetchLevelShools,
    initialSchools,
    fetchAcademicYears,
  } = schools;

  const {
    getAcademicTerms,
    academicTermsList,
    initialAcademicTerm,
    getAcademicYearsById,
    registrationDate,
    fetchingAcademicTerms,
  } = academicTerms;

  const { getHolidays } = holidays;

  const { getListChilds } = schedulePermissions;

  const { searchOneMap, listPostalCodes, searchPostalCode, initialPostalCode } =
    postalCodes;

  const {
    fetchingAdd,
    postAddStudent,
    getStudents,
    getDetailStudent,
    updateStudent,
    fetchingStudent,
    detailStudent,
    fetchingUpdate,
  } = students;

  const {
    postCalculationStudent,
    transactionsCostStudent,
    fetchingCostStudent,
    initialCaluclationCost,
  } = calculationCost;

  const [value, setValue] = useState("");

  /* Destructuring the useAddChild hook and assigning the formikAdd and formikEdit functions to the
formikAdd and formikEdit variables. */
  const { formikAdd, formikEdit, actionSave, actionCalculationCost } =
    useAddAndEditChild({
      postCalculationStudent,
      selectSchool,
      token,
      getListChilds,
      id,
      updateStudent,
      userId,
      academicTermsList,
      getAcademicYears,
      getAcademicYearsById,
      getLevelSchools,
      getListSchools,
      setSelectSchool,
      getDetailStudent,
      listPostalCodes,
      searchOneMap,
      searchPostalCode,
      postAddStudent,
    });

  useEffect(() => {
    initialCaluclationCost();
  }, [formikAdd.values]);

  useLayoutEffect(() => {
    initialPostalCode();
    initialAcademicTerm();
    initialSchools();
    initialCaluclationCost();
  }, []);

  return (
    <form className="ManageEditChild">
      <div className="ManageEditChild__childItemsMainWrapper">
        {!id ? (
          <>
            <div className="row g-0">
              <div
                className="ManageEditChild__back col-1"
                onClick={() => {
                  history.goBack();
                }}
              ></div>
              <h1 className="col-8 formElements__inputSectionHeader header--18 mt-2">
                Student Details
              </h1>
            </div>

            <TextInput
              title="LAST 4 CHARACTERS + ALPHABET (NRIC/FIN/PASSPORT) *"
              placeholder="Last 4 characters + Alphabet (NRIC/FIN/Passport)"
              {...formikAdd.getFieldProps("lastCharacter")}
              onChange={(evt) => {
                formikAdd.setFieldValue(
                  "lastCharacter",
                  evt.target.value.toUpperCase()
                );
              }}
              touched={formikAdd.touched.lastCharacter}
              error={formikAdd.errors.lastCharacter}
            ></TextInput>

            <ImageInput
              title="PROFILE IMAGE (.JPG/JPEG/PNG)"
              initialValue={formikAdd.values.photoUrl}
              onChangeCrop={(fileEncode) => {
                formikAdd.setFieldValue("photoUrl", fileEncode);
              }}
            ></ImageInput>

            <div className="row gx-5">
              <div className="col-sm-6">
                <TextInput
                  title="First Name *"
                  placeholder="Input First Name"
                  {...formikAdd.getFieldProps("firstName")}
                  touched={formikAdd.touched.firstName}
                  error={formikAdd.errors.firstName}
                ></TextInput>
              </div>
              <div className="col-sm-6">
                <TextInput
                  title="Last Name *"
                  placeholder="Input Last Name"
                  {...formikAdd.getFieldProps("lastName")}
                  touched={formikAdd.touched.lastName}
                  error={formikAdd.errors.lastName}
                ></TextInput>
              </div>
            </div>

            <div className="row g-5">
              <div className="col-sm-6">
                <DatePicker
                  fixedHeight
                  title="Date of Birth *"
                  onChange={(date) => {
                    formikAdd.setFieldValue("dob", date);
                  }}
                  selected={
                    formikAdd.values.dob
                      ? dayjs(formikAdd.values.dob).toDate()
                      : null
                  }
                  maxDate={new Date()}
                  error={formikAdd.errors.dob}
                  touched={formikAdd.touched.dob}
                  placeholder={"Select Date of Birth"}
                  mobileScreen
                ></DatePicker>
              </div>
              <div className="col-sm-6">
                <div className="js-multiInputWrapper js-isRequired w-100 formElements__group">
                  <label className="formElements__label">Gender *</label>
                  <div className="row">
                    <div className="col-sm-6">
                      <RadioBottom
                        name="gender"
                        value="male"
                        label="Male"
                        checked={formikAdd.values.gender === "male"}
                        onChange={() => {
                          formikAdd.setFieldValue("gender", "male");
                        }}
                      ></RadioBottom>
                    </div>
                    <div className="col-sm-6">
                      <RadioBottom
                        name="gender"
                        value="female"
                        label="Female"
                        checked={formikAdd.values.gender === "female"}
                        onChange={() => {
                          formikAdd.setFieldValue("gender", "female");
                        }}
                      ></RadioBottom>
                    </div>
                  </div>
                  {formikAdd.touched.gender && formikAdd.errors.gender ? (
                    <div className="formElements__errorText">
                      {formikAdd.errors.gender}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            <TextInput
              title="Medical Condition"
              placeholder="Input Medical Condition for Child"
              {...formikAdd.getFieldProps("medicalCondition")}
            ></TextInput>

            <div className="row g-0">
              <h1 className="pl-3 pr-3 formElements__inputSectionHeader header--18">
                School Details
              </h1>
            </div>
            <TextInput
              title="School *"
              placeholder="Input School"
              value={formikAdd.values.school}
              onChange={(evt) => {
                formikAdd.setFieldValue("school", evt.target.value);
              }}
              touched={formikAdd.touched.school}
              error={
                formikAdd.errors.school || listSchoolNames.length === 0
                  ? "School Not Found"
                  : undefined
              }
              dataList={listSchoolNames}
              list={"schools"}
              disabled={false}
            ></TextInput>

            <div className="flex flex-row">
              <DropDown
                title={"Academic Year *"}
                options={listAcademicYears}
                value={formikAdd.values.academyYear}
                disabled={
                  listAcademicYears.length === 0 ||
                  fetchAcademicYears === "pending"
                }
                onChange={(evt) => {
                  formikAdd.setFieldValue("academyYear", evt.target.value);
                  if (landing.token) {
                    getClassSchedules(
                      landing.token,
                      parseInt(evt.target.value, 10)
                    );
                    getAcademicTerms(
                      landing.token,
                      parseInt(evt.target.value, 10)
                    );
                    getHolidays(landing.token, parseInt(evt.target.value, 10));
                  }
                }}
                error={formikAdd.errors.academyYear}
                touched={formikAdd.touched.academyYear}
                isLoading={fetchAcademicYears === "pending"}
              ></DropDown>
            </div>

            <div className="row gx-5">
              <div className="col-sm-6 ">
                <DropDown
                  title={"Level *"}
                  options={listLevelSchools}
                  value={formikAdd.values.level}
                  disabled={
                    listLevelSchools.length === 0 ||
                    fetchLevelShools === "pending"
                  }
                  onChange={(evt) => {
                    formikAdd.setFieldValue("level", evt.target.value);
                    formikAdd.setFieldValue("class", "");
                  }}
                  error={formikAdd.errors.level}
                  isLoading={fetchLevelShools === "pending"}
                ></DropDown>
              </div>

              <div className="col-sm-6 ">
                <DropDown
                  title={"Class"}
                  options={listClassSchedules(formikAdd.values.level) || []}
                  value={formikAdd.values.class || ""}
                  onChange={(evt) => {
                    formikAdd.setFieldValue("class", evt.target.value);
                  }}
                  required={false}
                  error={formikAdd.errors.class}
                ></DropDown>
              </div>
            </div>

            {/* <TextInput
         title='MAC Address'
         placeholder='Input MAC Address'
         {...formikAdd.getFieldProps('macAddress')}
      ></TextInput> */}

            <ScheduleStudent
              formik={formikAdd}
              academicTermsList={academicTermsList || []}
              holidays={holidays}
              registrationDate={registrationDate}
              fetching={fetchingAcademicTerms === "pending"}
            />

            <PostalCode formik={formikAdd} listPostalCodes={listPostalCodes} />

            {transactionsCostStudent && (
              <CalculationCostResult
                transactionsCostStudent={transactionsCostStudent || undefined}
              />
            )}

            <div className="row gx-5">
              {transactionsCostStudent === null ? null : (
                <div className="col-sm-4">
                  <ButtonLoader
                    type="button"
                    className="w-100 mt-3"
                    isLoading={
                      fetchingAdd === "pending" || fetchingUpdate === "pending"
                    }
                    isDisabled={transactionsCostStudent === null}
                    onClick={() => {
                      // console.log("ds");
                      actionSave();
                    }}
                  >
                    Save
                  </ButtonLoader>
                </div>
              )}

              <div className="col-sm-4">
                <ButtonLoader
                  type="button"
                  className="w-100 mt-3"
                  isLoading={fetchingCostStudent === "pending"}
                  secondary
                  onClick={() => {
                    actionCalculationCost();
                  }}
                >
                  Calculate Cost
                </ButtonLoader>
              </div>
            </div>
          </>
        ) : (
          <>
            {detailStudent ? (
              <EditChildComponent
                formikEdit={formikEdit}
                fetchingAdd={fetchingAdd}
                fetchingUpdate={fetchingUpdate}
              />
            ) : (
              <div className="loaderWrapper">
                <div className="preloader preloaderBus" />
              </div>
            )}
          </>
        )}
      </div>
    </form>
  );
});

export default AddAndEditChild;
